// firebase-shim.js — Drop-in replacement per firebase/firestore
// Intercetta le chiamate Firestore e le redirige verso /api/v2/* del backend SQLite
// Permette migrazione zero-touch del codice esistente.
//
// Mappatura concettuale:
//   collection(db, "cantieri")          → CollectionRef("cantieri")
//   doc(db, "cantieri", "abc")          → DocRef("cantieri", "abc")
//   doc(db, "impostazioni", "azienda")  → ImpostazioniDocRef("azienda")
//   addDoc(coll, data)                  → POST   /api/v2/cantieri
//   setDoc(docRef, data)                → POST   /api/v2/coll  (con id forzato) o PUT impostazioni
//   updateDoc(docRef, patch)            → PATCH  /api/v2/coll/:id
//   deleteDoc(docRef)                   → DELETE /api/v2/coll/:id
//   getDoc(docRef)                      → GET    /api/v2/coll/:id
//   getDocs(query)                      → GET    /api/v2/coll?orderBy=...
//   onSnapshot(ref, cb)                 → polling ogni 5s
//   query(coll, orderBy("data","desc")) → coll wrapper con clausole
//   serverTimestamp()                   → new Date().toISOString()

// =====================================================
// CONFIG
// =====================================================
const POLL_INTERVAL_MS = 5000;
const API_BASE = '/api/v2';

function getToken() {
  try {
    return localStorage.getItem('bertoc_jwt') || '';
  } catch (e) { return ''; }
}

function authHeaders(extra = {}) {
  const token = getToken();
  const h = { ...extra };
  if (token) h['Authorization'] = 'Bearer ' + token;
  return h;
}

async function apiFetch(path, opts = {}) {
  const url = API_BASE + path;
  const headers = authHeaders(opts.headers || {});
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const r = await fetch(url, { ...opts, headers });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`API ${opts.method || 'GET'} ${path}: ${r.status} ${txt.slice(0,200)}`);
  }
  if (r.status === 204) return null;
  return r.json();
}

// =====================================================
// REFERENCE TYPES (oggetti opachi che il codice passa in giro)
// =====================================================

class CollectionRef {
  constructor(name) {
    this.type = 'collection';
    this.name = name;
    this.id = name;            // Firestore alias
    this.path = name;          // Firestore alias
    this.clauses = { orderBy: null, where: [], limit: null };
  }
}

class DocRef {
  constructor(coll, id) {
    this.type = 'doc';
    this.coll = coll;
    this.id = id;
    this.path = `${coll}/${id}`;
  }
}

// Caso speciale: doc(db, "impostazioni", "azienda")
class ImpostazioniDocRef {
  constructor(chiave) {
    this.type = 'impostazione';
    this.chiave = chiave;
    this.id = chiave;
    this.path = `impostazioni/${chiave}`;
  }
}

// QueryRef = collezione + clausole accumulate (orderBy, where, limit)
class QueryRef {
  constructor(coll, clauses) {
    this.type = 'query';
    this.coll = coll;
    this.clauses = clauses;
  }
}

// =====================================================
// FACTORY FUNCTIONS (le esportate)
// =====================================================

// collection(db, "cantieri")
export function collection(_db, name) {
  return new CollectionRef(name);
}

// doc(db, "coll", "id")  oppure  doc(collRef, "id")
export function doc(...args) {
  // Forma 1: doc(db, "coll", "id")
  if (args.length >= 3 && typeof args[1] === 'string') {
    const coll = args[1];
    const id = args[2];
    if (coll === 'impostazioni') return new ImpostazioniDocRef(id);
    return new DocRef(coll, id);
  }
  // Forma 2: doc(collRef, "id")
  if (args.length === 2 && args[0] instanceof CollectionRef) {
    return new DocRef(args[0].name, args[1]);
  }
  // Forma 3: doc(collRef) — auto-id (rara)
  if (args.length === 1 && args[0] instanceof CollectionRef) {
    return new DocRef(args[0].name, null);  // id verrà generato dal backend
  }
  throw new Error('doc(): firma non supportata: ' + JSON.stringify(args.map(a => typeof a)));
}

// query(collRef, orderBy(...), where(...), limit(...))
export function query(collRef, ...clauseObjects) {
  const clauses = { orderBy: null, where: [], limit: null };
  for (const c of clauseObjects) {
    if (!c) continue;
    if (c._kind === 'orderBy') clauses.orderBy = c;
    else if (c._kind === 'where') clauses.where.push(c);
    else if (c._kind === 'limit') clauses.limit = c.value;
  }
  return new QueryRef(collRef.name, clauses);
}

// orderBy("campo", "desc")
export function orderBy(field, dir = 'asc') {
  return { _kind: 'orderBy', field, dir };
}

// where("campo", "==", valore)
export function where(field, op, value) {
  return { _kind: 'where', field, op, value };
}

// limit(50)
export function limit(value) {
  return { _kind: 'limit', value };
}

// serverTimestamp() — usato nei dati prima di setDoc/addDoc
export function serverTimestamp() {
  return new Date().toISOString();
}

// =====================================================
// OPERAZIONI
// =====================================================

// addDoc(collRef, data) → crea con id auto
export async function addDoc(collRef, data) {
  if (!(collRef instanceof CollectionRef)) throw new Error('addDoc: serve CollectionRef');
  const created = await apiFetch(`/${collRef.name}`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
  // Firestore restituisce un DocumentReference con .id
  return new DocRef(collRef.name, created.id);
}

// setDoc(docRef, data, options?) → crea/sovrascrive a id specifico
export async function setDoc(docRef, data, options = {}) {
  if (docRef instanceof ImpostazioniDocRef) {
    return apiFetch(`/impostazioni/${docRef.chiave}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
  if (!(docRef instanceof DocRef)) throw new Error('setDoc: serve DocRef');
  // {merge:true} → patch; default → sovrascrivi (ma il backend fa sempre merge JSON, va bene così)
  if (options.merge) {
    return apiFetch(`/${docRef.coll}/${docRef.id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }
  // sovrascrittura totale: prima delete (ignora errori) poi insert con id
  try { await apiFetch(`/${docRef.coll}/${docRef.id}`, { method: 'DELETE' }); } catch (e) {}
  return apiFetch(`/${docRef.coll}`, {
    method: 'POST',
    body: JSON.stringify({ ...data, id: docRef.id })
  });
}

// updateDoc(docRef, patch)
export async function updateDoc(docRef, patch) {
  if (docRef instanceof ImpostazioniDocRef) {
    // Per impostazioni il PUT è già upsert; carichiamo, mergiamo, riscriviamo
    const current = await apiFetch(`/impostazioni/${docRef.chiave}`).catch(() => ({}));
    return apiFetch(`/impostazioni/${docRef.chiave}`, {
      method: 'PUT',
      body: JSON.stringify({ ...current, ...patch })
    });
  }
  if (!(docRef instanceof DocRef)) throw new Error('updateDoc: serve DocRef');
  return apiFetch(`/${docRef.coll}/${docRef.id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
}

// deleteDoc(docRef)
export async function deleteDoc(docRef) {
  if (docRef instanceof ImpostazioniDocRef) {
    // Non implementato lato backend (le impostazioni non si cancellano)
    console.warn('deleteDoc su impostazioni non supportato:', docRef.chiave);
    return;
  }
  if (!(docRef instanceof DocRef)) throw new Error('deleteDoc: serve DocRef');
  return apiFetch(`/${docRef.coll}/${docRef.id}`, { method: 'DELETE' });
}

// getDoc(docRef) → { exists(), data(), id }
export async function getDoc(docRef) {
  let data, id, exists;
  if (docRef instanceof ImpostazioniDocRef) {
    data = await apiFetch(`/impostazioni/${docRef.chiave}`).catch(() => null);
    id = docRef.chiave;
    exists = !!data;
  } else if (docRef instanceof DocRef) {
    data = await apiFetch(`/${docRef.coll}/${docRef.id}`).catch(() => null);
    id = docRef.id;
    exists = !!data;
  } else {
    throw new Error('getDoc: serve DocRef');
  }
  return {
    exists: () => exists,
    data: () => data || undefined,
    id,
    ref: docRef
  };
}

// getDocs(queryOrColl) → QuerySnapshot { forEach, docs, size, empty }
export async function getDocs(qOrColl) {
  const url = buildListUrl(qOrColl);
  const docs = await apiFetch(url);
  const docArray = (docs || []).map(d => ({
    id: d.id,
    data: () => d,
    ref: new DocRef(getCollName(qOrColl), d.id),
    exists: () => true
  }));
  return {
    forEach: (fn) => docArray.forEach(fn),
    docs: docArray,
    size: docArray.length,
    empty: docArray.length === 0
  };
}

// onSnapshot(ref, callback) → polling
// Firma reale Firebase: onSnapshot(ref, observer) o onSnapshot(ref, onNext, onError)
export function onSnapshot(qOrColl, onNext, onError) {
  let cancelled = false;
  let lastHash = null; // dedup: chiama onNext solo se i dati sono cambiati

  // Caso DocRef: polling singolo doc
  if (qOrColl instanceof DocRef || qOrColl instanceof ImpostazioniDocRef) {
    const fetchOnce = async () => {
      if (cancelled) return;
      try {
        const snap = await getDoc(qOrColl);
        if (cancelled) return;
        const hash = snap.exists() ? JSON.stringify(snap.data()) : '__NOT_EXISTS__';
        if (hash !== lastHash) {
          lastHash = hash;
          onNext(snap);
        }
      } catch (e) {
        if (!cancelled && onError) onError(e);
      }
    };
    fetchOnce();
    const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }

  // Caso Collection/Query: polling lista
  const fetchOnce = async () => {
    if (cancelled) return;
    try {
      const snap = await getDocs(qOrColl);
      if (cancelled) return;
      const hash = JSON.stringify(snap.docs.map(d => ({ id: d.id, data: d.data() })));
      if (hash !== lastHash) {
        lastHash = hash;
        onNext(snap);
      }
    } catch (e) {
      if (!cancelled && onError) onError(e);
    }
  };
  fetchOnce();
  const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);
  return () => { cancelled = true; clearInterval(interval); };
}

// =====================================================
// HELPERS
// =====================================================
function getCollName(qOrColl) {
  if (qOrColl instanceof CollectionRef) return qOrColl.name;
  if (qOrColl instanceof QueryRef) return qOrColl.coll;
  return null;
}

function buildListUrl(qOrColl) {
  let coll, clauses;
  if (qOrColl instanceof CollectionRef) {
    coll = qOrColl.name; clauses = qOrColl.clauses;
  } else if (qOrColl instanceof QueryRef) {
    coll = qOrColl.coll; clauses = qOrColl.clauses;
  } else {
    throw new Error('getDocs/onSnapshot: serve CollectionRef o QueryRef');
  }
  const params = [];
  if (clauses && clauses.orderBy) {
    params.push('orderBy=' + encodeURIComponent(`${clauses.orderBy.field} ${clauses.orderBy.dir}`));
  }
  if (clauses && clauses.limit) {
    params.push('limit=' + clauses.limit);
  }
  if (clauses && clauses.where && clauses.where.length) {
    for (const w of clauses.where) {
      if (w.op === '==') params.push(`${encodeURIComponent(w.field)}=${encodeURIComponent(w.value)}`);
      // altri operatori: per ora ignorati (non usati nel codice)
    }
  }
  return `/${coll}` + (params.length ? '?' + params.join('&') : '');
}
